import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { useAttendancePolicies } from 'modules/attendance/lib/attendancePolicies'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'

import AddObservationsModal from './AddObservationsModal'

interface Props {
  date: moment.Moment
  store: Store
  shifts: TimesheetPageShiftFragment[]
}

const OptionsButton = ({ date, shifts, store }: Props) => {
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)

  const createdShifts = shifts.filter((shift) => !AttendanceShiftUtils.isNew(shift))
  const { attendancePeriods, reviewsStats } = useTimesheetData()
  const { startsOn } = useContractsVersion()
  const { data } = useAttendancePolicies()
  const canEditAttendance = !!data?.employee?._policies?.editAttendance?.can
  const periods = attendancePeriods ?? []

  const shiftsWithReadonly = createdShifts.map((shift) => {
    const shiftPeriod = AttendanceShiftUtils.getPeriodForShift(shift, periods)

    const readonly = AttendanceShiftUtils.isReadOnly({
      date: moment(shift.date),
      approvedDates: reviewsStats?.approvedDates ?? [],
      currentPeriod: shiftPeriod ? [shiftPeriod] : [],
      startsOn,
      canEditAttendance,
    })

    return { ...shift, readonly }
  })

  const onClickOpenModal = () => setModalOpen(true)

  const onCloseModal = () => setModalOpen(false)

  const openModal = () => {
    const sortedShifts = shifts.sort((a, b) => a?.halfDay?.localeCompare(b?.halfDay ?? '') ?? 0)

    const modalProps = {
      onClose: onCloseModal,
      shifts: sortedShifts,
      date,
      store,
    }

    return <AddObservationsModal {...modalProps} />
  }

  if (modalOpen) {
    return openModal()
  }

  if (!shiftsWithReadonly.some((shift) => !shift.readonly)) return null

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" aria-label="popover trigger" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="end">
        <PopoverMenu.TouchableOption
          key="observation"
          title={i18n.t('clock_in.observations.add_edit.title')}
          description={i18n.t('clock_in.observations.description')}
          onClick={() => onClickOpenModal()}
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default observer(OptionsButton)
